import { useCallback, useState } from 'react';
import type { ConfiguredService } from '../types/service.types';
import { isLastCompletedServiceError } from '../utils/lastCompletedServiceFieldErrors';
import type { SmriqJourneyError } from './useSmriqJourney';

interface LastCompletedServiceStatus {
    selectionWithdrawn: boolean;
    canGeneratePlan: boolean;
    selectionValid: boolean;
}

interface UseSmriqLastServiceEditArgs {
    configuredServices: readonly ConfiguredService[];
    lastCompleted: LastCompletedServiceStatus;
    generateError: SmriqJourneyError | null;
    /** A settled plan to return to; without one an open edit must be completed, not dismissed. */
    cancellable: boolean;
    planFieldsEdited: boolean;
    revertSelection: () => void;
    discardEdit: () => void;
    resumeInterrupted: () => void;
}

/** Shows or hides the last completed service form behind its summary line, as the vehicle and
 *  plan-parameter sections do; a fresh plan collapses it again. */
export function useSmriqLastServiceEdit({
    configuredServices,
    lastCompleted,
    generateError,
    cancellable,
    planFieldsEdited,
    revertSelection,
    discardEdit,
    resumeInterrupted,
}: UseSmriqLastServiceEditArgs) {
    const [requested, setRequested] = useState(false);
    const [seenServices, setSeenServices] = useState(configuredServices);
    if (configuredServices !== seenServices) {
        setSeenServices(configuredServices);
        setRequested(false);
    }

    // A withdrawn, rejected or blocking selection is something the dealer has to see.
    const needsAttention =
        lastCompleted.selectionWithdrawn ||
        !lastCompleted.canGeneratePlan ||
        !lastCompleted.selectionValid ||
        isLastCompletedServiceError(generateError);
    // Latched open so a valid replacement still shows its Update until a fresh plan arrives.
    if (needsAttention && !requested) setRequested(true);

    const requestEdit = useCallback(() => setRequested(true), []);

    const cancel = useCallback(() => {
        setRequested(false);
        revertSelection();
        // An unsent duration/MOT draft still needs its Update, so only this section's edit is dropped.
        if (!planFieldsEdited) discardEdit();
        resumeInterrupted();
    }, [revertSelection, planFieldsEdited, discardEdit, resumeInterrupted]);

    return {
        expanded: requested || needsAttention,
        requestEdit,
        cancelEdit: requested && cancellable && !needsAttention ? cancel : undefined,
    };
}
